<script>
    export default {
        name: 'EcomProductCard',
        props: {
            productId: {
                type: Number,
                required: true,
            },
            productName: {
                type: String,
                required: true,
            },
            productPageUrl: {
                type: String,
                required: true,
            },
            priceDetails: {
                type: Object,
                required: true,
            },
            categories: {
                type: Array,
                required: true,
            },
            images: {
                type: Array,
                required: true,
            },
            canAddToCart: {
                type: Boolean,
                required: false,
                default: true,
            }
        },
        data() {
            return {
                isAddingToCart: false,
                addToCartSuccess: false,
            };
        },
        methods: {
            async addToCart() {
                if (this.isAddingToCart) {
                    return;
                }

                this.isAddingToCart = true;


                this.addToCartSuccess = false;

                try {
                    const { data } = await this.$_http({
                        url: '/api/1.0/carts/my/products/',
                        method: 'POST',
                        data: {
                            products: [
                                {
                                    productId: this.productId,
                                    quantity: 1,
                                }
                            ]
                        }
                    });

                    if (!data.success) {
                        const errorTranslation = data.products?.[0]?.errors?.[0]?.message ?? 'addingProductToCartFailed';
                        this.$_swal({
                            title: this.$_locale(errorTranslation),
                            icon: 'warning',
                            confirmButtonText: this.$_locale('ok'),
                        });
                        return;
                    }

                    this.addToCartSuccess = true;

                    const cartResponse = await this.$_http.get('/api/1.0/carts/my/products/');
                    const rows = Array.isArray(cartResponse.data.rows) ? cartResponse.data.rows : [];

                    const totalQuantity = rows.reduce((sum, row) =>
                        sum + (Number(row.quantity) || 1), 0
                    );

                    const totalSum = rows.reduce((sum, row) =>
                        sum + ((Number(row.quantity) || 1) * (Number(row.price) || 0)), 0
                    );

                    window.dispatchEvent(new CustomEvent('cart-updated', {
                        detail: { count: totalQuantity, totalFormatted: this.$_formatCurrency(totalSum) + '\u00a0' + this.$_CURRENCY_SYMBOL }
                    }));

                    // widget
                    const widgetData = {
                        ...data,
                        products: (data.products || []).map(p => ({
                            ...p,
                            productPageUrl: this.productPageUrl,
                        })),
                    };

                    document.body.dispatchEvent(
                        new CustomEvent('wisenetwork:addToCartSuccess', {
                            detail: widgetData
                        })
                    );
                } catch (error) {
                    console.error('Add to cart failed:', error);

                    this.$_swal({
                        title: this.$_locale('addingProductToCartFailed'),
                        text: error?.message || '',
                        icon: 'error',
                        confirmButtonText: this.$_locale('ok'),
                    });
                } finally {
                    this.isAddingToCart = false;
                }
            },
        },
        mounted() {
            // Does the product card have an image carousel? (= product has at least two images)
            if (this.$refs.productImageCarouselSlides && this.$refs.productImageCarouselButtons) {
                // Activate first carousel slide. Without this, carousel won't show any images.
                this.$refs.productImageCarouselSlides[0].classList.add('active');
                this.$refs.productImageCarouselButtons[0].classList.add('active');
                // The 'active' class cannot be set directly in the template, because we don't want Vue to re-add the class if Bootstrap removes it.
            }
        },
    };
</script>

<template>
    <div class="card card-product">
    
        <template v-if="images.length >= 2">
            <!-- Product has multiple images -->
            <div :id="'card-product-slider-'+productId" class="carousel slide card-product-carousel" data-bs-ride="carousel" data-bs-interval="false">
                <div class="carousel-indicators">
                    <!-- imageIndex starts from 0 and is used for Bootstrap. -->
                    <button
                        v-for="(image, imageIndex) in images"
                        type="button"
                        :data-bs-target="'#card-product-slider-'+productId"
                        :data-bs-slide-to="imageIndex"
                        aria-current="true"
                        :aria-label="image.altText"
                        ref="productImageCarouselButtons"
                    ></button>
                </div>
                <div class="carousel-inner">
                    <template v-for="image in images">
                        <div class="carousel-item" ref="productImageCarouselSlides">
                            <a :href="productPageUrl">
                                <img
                                    :src="image.url"
                                    class="d-block w-100"
                                    :alt="image.altText">
                            </a>
                        </div>
                    </template>
                </div>
                <button class="carousel-control-prev" type="button" :data-bs-target="'#card-product-slider-'+productId" data-bs-slide="prev">
                    <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                    <span class="visually-hidden">{{ $_locale('previous') }}</span>
                </button>
                <button class="carousel-control-next" type="button" :data-bs-target="'#card-product-slider-'+productId" data-bs-slide="next">
                    <span class="carousel-control-next-icon" aria-hidden="true"></span>
                    <span class="visually-hidden">{{ $_locale('next') }}</span>
                </button>
            </div>
        
        </template>
        <template v-else-if="images.length === 1">
            <!-- Product has one image. -->
            <a :href="productPageUrl">
                <img
                    :src="images[0].url"
                    class="card-img-top card-product-img"
                    :alt="images[0].altText">
            </a>
        </template>
    
        <div class="card-body">
            <h3 class="card-title">
                <a :href="productPageUrl">
                    {{ productName }}
                </a>
            </h3>
            <div class="badge-container">
                <span v-for="category in categories" class="badge bg-primary position-relative">
                    <a :href="category.categoryPageUrl" class="stretched-link">
                        {{ category.categoryName }}
                    </a>
                </span>
            </div>
            <div class="card-product-pricing">
                <div class="card-product-price">
                    <template v-if="priceDetails.showStartingPrice">
                        <span class="pre-price-text">{{ $_locale('From') }}</span>
                    </template>
                    
                    <template v-if="priceDetails.isOnSale">
                    
                        <span class="sale-price"> <!-- Sale price, only shown when on sale -->
                            <span class="price-value">{{ $_formatCurrency(priceDetails.actualPrice) }}</span>
                            <span class="price-symbol">{{ $_CURRENCY_SYMBOL }}</span>
                        </span>
                        <span class="original-price"> <!-- Original price, only shown when on sale -->
                            <span class="price-value">{{ $_formatCurrency(priceDetails.originalPrice) }}</span>
                            <span class="price-symbol">{{ $_CURRENCY_SYMBOL }}</span>
                        </span>
                        
                    </template>
                    <template v-else>
                    
                        <span class="regular-price"> <!-- Regular price, only shown when not on sale -->
                            <span class="price-value">{{ $_formatCurrency(priceDetails.actualPrice) }}</span>
                            <span class="price-symbol">{{ $_CURRENCY_SYMBOL }}</span>
                        </span>
                    
                    </template>
                </div>
                <template v-if="priceDetails.isOnSale">
                    <div class="card-product-sale-percentage text-success">
                        {{ $_locale('sale') }}
                        {{ priceDetails.salePercent }} %
                    </div>
                </template>
            </div>
            
            <!-- TODO: If product has variants...
                <div class="card-product-variations">
                    <ul class="card-product-variation-list card-product-variation-list-size">
                        <li>M</li>
                        <li>L</li>
                        <li>XL</li>
                        <li>XXL</li>
                    </ul>
                    <ul class="card-product-variation-list card-product-variation-list-color">
                        <li aria-label="Harmaa"></li>
                        <li aria-label="Musta"></li>
                    </ul>
                </div>
            -->
            
            <div class="card-product-actions">
                <button
                    v-if="canAddToCart"
                    type="button"
                    class="btn btn-primary btn-add-to-cart"
                    :disabled="isAddingToCart"
                    @click="addToCart">
                    {{ isAddingToCart ? $_locale('addingToCart') : $_locale('addToCart') }}
                </button>

                <a
                    v-else
                    :href="productPageUrl"
                    class="btn btn-primary btn-select-options">
                    {{ $_locale('goToChoose') }}
                </a>
            </div>
        </div>
    </div>
</template>
